<template>
  <div
    :class="[
      'page-list',
      'page-list--hamac-project',
    ]"
  >

    <div
      v-hide="
        fetchState.status.value !== 'success' ||
        providerCallCount === 0
      "
      class="page-list__subfilters"
    >
      <ui-wad class="font-weight-light font-size-90">
        <span
          v-if="context.total > 0"
        >
          <layout-misc-float
            :value="context.total"
          />
          {{ context.total <= 1 ? 'projet' : 'projets' }}
        </span>
        <span
          v-if="context.total === 0"
        >
          Aucun projet trouvé
        </span>
      </ui-wad>

      <ui-button-link
        v-if="hasAnyCriterionSet"
        class="font-size-90"
        :variant="UI.VARIANT.SECONDARY"
        underline="hover"
        @click="resetCriterion"
      >
        <ui-icon
          name="filter_alt_off"
        />
        supprimer les filtres
      </ui-button-link>
    </div>


    <b-card
      :class="[
        'page-list__filters',
        (
          fetchState.status.value !== 'success' ||
          providerCallCount === 0
        ) ? 'page-list__filters--busy' : '',
      ]"
      body-class="p-1 d-flex justify-content-space-between"
    >

      <!-- Filters: first column -->
      <ui-wad class="d-flex align-items-center gap-1 flex-grow-1">

        <ui-form-inner-icon
          :size="UI.SIZE.SMALL"
          name="search"
          class="w-1/4"
        >
          <ui-form-input
            :model-value="context.search"
            placeholder="Rechercher"
            data-cy="search"
            :size="UI.SIZE.SMALL"
            @update:model-value="debouncedSearchChange"
          />
        </ui-form-inner-icon>

        <ui-chip-select-multiple
          v-model="context.filters.tags"
          data-cy="tags-filter"
          :options="availableTags"
          hoistable
          searchable
        >
          Tags
        </ui-chip-select-multiple>

        <ui-chip-toggle
          v-model="context.filters.show_archived"
          data-cy="show_archived"
        >
          <template #default>
            <ui-icon
              class="mr-0.5"
              name="archive"
            />&nbsp;Archivés
          </template>
        </ui-chip-toggle>

      </ui-wad>

      <!-- Filter: second column -->
      <ui-wad class="d-flex gap-1 align-items-center">

        <ui-tooltip-wrapper
          class="d-inline-block"
          v-bind="{
            title: `Vous n'avez pas les droits pour effectuer cette action`,
            variant: UI.VARIANT.LIGHT,
          }"
          :status="hasACL([
              ACLS.HAMAC_EQUIPE_GET,
              ACLS.HAMAC_PROFESSION_GET,
              ACLS.HAMAC_PROFESSION_ATTACH_TO_PROJET,
              ACLS.HAMAC_PROJET_CREATE,
              ACLS.HAMAC_PROJET_GET,
              ACLS.HAMAC_PROJET_UPDATE,
              ACLS.HAMAC_CONFIG_DONNEES_PERSO_GET,
              ACLS.HAMAC_NUMERO_OBJET_ATTACH_TO_PROJET,
              ACLS.HAMAC_NUMERO_OBJET_GET,
              ACLS.HAMAC_ANALYTIQUE_ATTACH_TO_PROJET,
              ACLS.HAMAC_ANALYTIQUE_GET,
              ACLS.HAMAC_RUBRIQUE_ATTACH_TO_PROJET
            ]) ?
            UI.STATUS.DISABLED :
            UI.STATUS.ENABLED
          "
        >
          <layout-cta-sub
            component="ui-link-button"
            :variant="UI.VARIANT.PRIMARY"
            :route="getCreationRoute()"
            :size="UI.SIZE.SMALL"
            :status="hasACL([
              ACLS.HAMAC_EQUIPE_GET,
              ACLS.HAMAC_PROFESSION_GET,
              ACLS.HAMAC_PROFESSION_ATTACH_TO_PROJET,
              ACLS.HAMAC_PROJET_CREATE,
              ACLS.HAMAC_PROJET_GET,
              ACLS.HAMAC_PROJET_UPDATE,
              ACLS.HAMAC_CONFIG_DONNEES_PERSO_GET,
              ACLS.HAMAC_NUMERO_OBJET_ATTACH_TO_PROJET,
              ACLS.HAMAC_NUMERO_OBJET_GET,
              ACLS.HAMAC_ANALYTIQUE_ATTACH_TO_PROJET,
              ACLS.HAMAC_ANALYTIQUE_GET,
              ACLS.HAMAC_RUBRIQUE_ATTACH_TO_PROJET
            ]) ?
              UI.STATUS.ENABLED :
              UI.STATUS.DISABLED
            "
            data-cy="create-project-cta"
          >
            <ui-icon
              name="add"
            />
            &nbsp;
            Ajouter un projet
          </layout-cta-sub>
        </ui-tooltip-wrapper>
      </ui-wad>

    </b-card>


    <ui-table-wrapper
      ref="table"
      :provider="provider"
      :fields="fields"
      :initial-sort-by="context.order"
      :initial-sort-desc="context.sort === 'desc'"
      :tbody-tr-class="tbodyRowClass"
      :hide-header="providerCallCount === 0"
      primary-key="uuid_hamac_projet"
      hover
      striped
      @row-clicked="({ item }) => openDetailsIntent(item)"
    >
      <template v-if="providerCallCount === 0" #table-busy>
        <layout-placeholder-table-body
          :count="context.perPage"
          striped
        />
      </template>

      <template #cell(count)="data">
        <div
          v-if="data.item.count === 0"
          :class="[
            'page-list__count__text',
            'page-list__count__text--nil'
          ]"
        >
          &mdash;
        </div>
        <div
          v-else
          class="page-list__count__text"
        >
          {{ data.value }}
        </div>
      </template>

      <template #cell(actions-left)="data">
        <div class="page-list__actions-left__inner">

          <ui-button-icon
            :theme="UI.ICON_THEME.OUTLINED"
            :name="
              isItemSelected(data.item) ?
                'check_box' :
                'check_box_outline_blank'
            "
            :size="UI.SIZE.SMALL"
            @click="toggleItem(data.item)"
          />

          <ui-link-icon
            :class="[
              'page-list__open-cta',
              'font-size-20px'
            ]"
            name="arrow_outward"
            :route="getDetailsRoute(data.item.uuid_hamac_projet)"
            :elevation="UI.ELEVATION.LEVEL_0"
            no-animation
          />

        </div>
      </template>

      <template #cell(actions-right)="data">
        <div class="page-list__actions-right__inner">

          <ui-buffered-pending-icon
            :is-pending="isRowPending(data.item.uuid_hamac_projet)"
            :type="UI.SPINNER_TYPE.BORDER"
            :size="UI.SIZE.SMALL"
            :sustain-phase-duration="400"
          >
            <template #default="{ onBufferPending }">
              <ui-dropdown-wrapper
                :menu-variant="UI.VARIANT.PRIMARY"
                data-cy="action_menu"
                :data-key="data.item.uuid_hamac_projet"
                placement="right-start"
              >
                <template #button-content>
                  <ui-button-icon
                    :status="onBufferPending || isRowDisabled(data.item.uuid_hamac_projet) ?
                      UI.STATUS.DISABLED :
                      UI.STATUS.ENABLED
                    "
                    name="more_vert"
                    :variant="UI.VARIANT.DARK"
                    :size="UI.SIZE.SMALL"
                    square
                    ripple
                    data-cy="action_button"
                    :data-key="data.item.uuid_hamac_projet"
                  />
                </template>

                <template #default>

                  <b-dropdown-item
                    :disabled="!hasACL(ACLS.HAMAC_PROJET_GET)"
                    :to="getDetailsRoute(data.item.uuid_hamac_projet)"
                    data-cy="open_project_button"
                  >
                    <ui-icon
                      name="file_open"
                    />
                    Consulter le projet
                  </b-dropdown-item>

                  <b-dropdown-item
                    :disabled="!hasACL(ACLS.HAMAC_PROJET_CREATE)"
                    :to="getDuplicationRoute(data.item.uuid_hamac_projet)"
                    data-cy="duplicate_project_button"
                    :data-key="data.item.uuid_hamac_projet"
                  >
                    <ui-icon
                      name="file_copy"
                    />
                    Dupliquer le projet
                  </b-dropdown-item>

                  <b-dropdown-item
                    :disabled="!hasACL(ACLS.HAMAC_PROJET_GET)"
                    data-cy="export_project_button"
                    @click="exportCallback(data.item)"
                  >
                    <ui-icon
                      name="file_download"
                    />
                    Exporter les données du projet
                  </b-dropdown-item>
                  <b-dropdown-item
                    :disabled="!hasACL(ACLS.HAMAC_PROJET_ARCHIVED) || data.item.archived"
                    data-cy="action_archive"
                    @click="archiveCallback(data.item)"
                  >
                    <ui-icon
                      name="archive"
                    />
                    Archiver le projet sélectionné
                  </b-dropdown-item>
                </template>

              </ui-dropdown-wrapper>
            </template>
          </ui-buffered-pending-icon>

        </div>
      </template>


      <template #cell(tags)="data">
        <ui-wad class="d-flex flex-wrap gap-1">
          <b-badge
            v-for="tag in data.item.tags"
            :key="`${data.item.uuid_hamac_projet}-${tag}`"
            :variant="UI.VARIANT.LIGHT"
            :class="[
              'page-list__tag-item',
              {
                'page-list__tag-item--tone-downed': isTagToneDowned(tag)
              }
            ]"
            href="#"
            @click.stop="toggleTag(tag)"
          >
            {{ tag }}
          </b-badge>
        </ui-wad>
      </template>
      <template #cell(infos)="data">
        <ui-badge-light class="page-list__infos__badge">
          <ui-icon
            :class="[
              'page-list__infos__icon',
              {
                'page-list__infos__icon--active':
                   data.item.notifications &&
                  !data.item.archived
              },
            ]"
            name="notifications"
          />
          <ui-icon
            :class="[
              'page-list__infos__icon',
              {
                'page-list__infos__icon--active': data.item.archived
              },
            ]"
            name="archive"
          />
        </ui-badge-light>

      </template>
      <template #head(infos)="data">
        {{ data.label }}
        <ui-popover-wrapper
          popover-class="page-list__popover"
          :size="UI.SIZE.SMALL"
        >
          <template #default>
            <ui-icon
              class="font-size-120 color-gray-dark vertical-align-top"
              name="help_outline"
            />
          </template>
          <template #popover>
            <div class="page-list__popover__block">
              <ui-icon
                name="notifications"
                :class="[
                  'page-list__popover__icon',
                ]"
              />
              Notification<span class="font-size-90">(s)</span> à traiter
            </div>
            <div class="page-list__popover__block">
              <ui-icon
                name="archive"
                class="page-list__popover__icon"
              />
              Projet archivé
            </div>
          </template>
        </ui-popover-wrapper>
      </template>
    </ui-table-wrapper>


    <div class="page-list__footer">

      <ui-blank />

      <ui-table-pagination
        v-model="context.currentPage"
        :total-rows="context.total"
        :per-page="context.perPage"
        align="center"
      />

      <ui-wad class="d-flex align-items-center gap-1 flex-shrink-0">
        <ui-wad class="font-size-90 font-weight-light">
          par page
        </ui-wad>
        <ui-form-select
          v-model="context.perPage"
          :options="perPageOptions"
          :size="UI.SIZE.SMALL"
          class="page-list__filters__per-page"
          data-cy="per-page-filter"
        />
      </ui-wad>

    </div>

    <teleport defer to="[data-teleport='footer']">
      <div
        :class="[
          'page-list__action-footer',
          hasDoubleClicked ? 'd-none' : ''
        ]"
      >

        <transition
          enter-active-class="animate__animated animate__slideInUp ease-in-out a1 d1"
          leave-active-class="animate__animated animate__slideOutDown ease-in-out a1"
        >
          <div
            v-if="selectedItems.length"
            class="page-list__action-footer__inner"
          >

            <ui-wad class="font-weight-bolder font-size-80 ml-4 mr-4 text-uppercase">
              Actions groupées
            </ui-wad>

            <ui-button
              outline
              class="page-list__action-footer__cta h-100 px-3.5"
              data-cy="export_multiple_button"
              :size="UI.SIZE.MEDIUM"
              :status="exportButtonPending ? UI.STATUS.DEFFERED : UI.STATUS.ENABLED"
              :variant="UI.VARIANT.SECONDARY"
              @click="() => exportMultiple(selectedItems)"
            >
              <ui-icon
                name="file_download"
              />
              Exporter
            </ui-button>

            <ui-tooltip-wrapper
              class="d-inline-block"
              v-bind="
                !hasACL(ACLS.HAMAC_PROJET_ARCHIVED) ?
                  {
                    title: `Vous n'avez pas les droits pour effectuer cette action`,
                    customClass: 'font-size-80 my-1',
                    variant: UI.VARIANT.LIGHT,
                  } :
                  {
                    title: `Certains projets sélectionnés ne sont pas archivables`,
                    variant: UI.VARIANT.DARK,
                  }
              "
              :status="
                (
                  selectionHasArchivedProjects ||
                  !hasACL(ACLS.HAMAC_PROJET_ARCHIVED)
                ) ?
                  UI.STATUS.ENABLED :
                  UI.STATUS.DISABLED
              "
            >
              <ui-button
                class="page-list__action-footer__cta h-100 px-3.5 mx-2"
                data-cy="archive_multiple_button"
                :size="UI.SIZE.MEDIUM"
                :status="archiveButtonPending ?
                  UI.STATUS.DEFFERED :
                  selectionHasArchivedProjects ?
                    UI.STATUS.DISABLED :
                    hasACL(ACLS.HAMAC_PROJET_ARCHIVED) ?
                      UI.STATUS.ENABLED :
                      UI.STATUS.DISABLED
                "
                :variant="UI.VARIANT.SECONDARY"
                outline
                @click="() => archiveMultiple(selectedItems)"
              >
                <ui-icon
                  name="archive"
                />
                Archiver
              </ui-button>
            </ui-tooltip-wrapper>

            <counter
              :count="selectedItems.length"
              :print="count => count > 1 ?
                `projets sélectionnés` :
                `projet sélectionné`
              "
              class="mr-6 text-transform-uppercase font-weight-bolder font-size-80 line-height-2.0"
            />

            <ui-wad class="d-flex align-items-center gap-2">
              <ui-button-link
                :variant="UI.VARIANT.SECONDARY"
                class="font-size-90"
                underline="hover"
                bulky
                @click="unselectAll"
              >
                <ui-icon name="clear" />
                Enlever toute ma sélection
              </ui-button-link>
            </ui-wad>

          </div>
        </transition>

      </div>
    </teleport>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members
@import ~/assets/sass/modules/form/variables

$actions-column-width: 60px
$count-column-width: 110px
$infos-column-width: 6vw
$date-column-width: 180px
$team-column-width: 180px

.page-list
  $self: &

  padding-top: $layout-spacer-y * .75
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y
  flex-grow: 1

  &__popover

    &__block
      margin-top: space-x(.5)
      margin-bottom: space-x(0.5)
      color: $gray-deep

      &:not(:first-child)
        margin-top: space-x(1)

    &__icon
      @include led($primary)
      font-size: 140%
      margin-right: $card-spacer-x
      line-height: 0

  &__action-footer
    position: sticky
    width: 100%
    bottom: 0
    left: 0

    &__inner
      background-color: $background-primary
      padding: $card-spacer-y $card-spacer-x
      border-top: 1px solid $border-color
      box-shadow: $shadow-discreet
      display: flex
      align-items: center
      // ("Law of Separation" between footer and lateral menu)
      border-left: 1px solid $gray-97

  &__header
    margin-top: $layout-spacer-y
    margin-bottom: $layout-spacer-y
    display: flex
    justify-content: flex-end
    align-items: center

  &__project-actions
    margin-bottom: $layout-spacer-y

  &__subfilters
    display: flex
    gap: $card-spacer-x
    align-items: baseline
    margin-bottom: $layout-spacer-y * .25
    padding-left: space-x(1.5)
    padding-right: space-x(1.5)
    animation: fade-in 150ms ease-in-out

  &__filters
    margin-bottom: $layout-spacer-y

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

    &__project
      min-width: calc(100% / 5)

    &__tags
      min-width: calc(100% / 3)

    &__archived
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0
      vertical-align: middle
      padding-top: 2px

    &__per-page
      width: auto
      font-size: 85%
      height: $simulated-form-control-height-md

  &__footer
    margin-top: $layout-spacer-y
    display: flex
    align-items: center
    justify-content: space-between

  /////////////////////////////////

  &__tag-item
    @include precede
      font-size: 105%
      font-weight: $font-weight-normal

      &--tone-downed
        opacity: 0.25

  /////////////////////////////////

  :deep(.b-table.table)
    @include table-presets
    @include table-row-hovering( #{ $self }__open-cta )

    /// Columns
    #{ $self }__actions-left,
    #{ $self }__actions-right
      min-width: $actions-column-width

    #{ $self }__infos
      min-width: $infos-column-width
      text-align: center
      text-wrap: nowrap

    #{ $self }__count
      min-width: $count-column-width

    #{ $self }__date
      min-width: $date-column-width

    #{ $self }__team
      min-width: $team-column-width

    /// Cells
    tbody

      #{ $self }__actions-left
        @include table-cell-presets
          gap: space-x(1)
          align-items: center

      #{ $self }__actions-right
        @include table-cell-presets
          gap: space-x(.5)
          justify-content: flex-end
          flex-direction: row-reverse

      #{ $self }__team
        font-size: 90%
        color: $gray-deep

      #{ $self }__date
        word-spacing: -1.5px
        color: $gray-deep

      #{ $self }__tags
        font-size: 85%

      #{ $self }__infos
        vertical-align: middle
        width: 3.5vw
        padding: space-x(.5) 0

        &__badge
          padding: space-x(.25) space-x(1)
          background-color: brighten($gray-95, 70%)

        &__icon
          @include led($gray-bright)
          font-size: 18px

          &:not(:last-child)
            margin-right: space-x(.5)

          &--active
            @include led($primary)

      #{ $self }__count

        &__text
          $width: 56px

          color: $gray-deep
          font-family: $font-family-monospace
          width: $width
          text-align: right
          letter-spacing: 0.4px
          font-weight: $font-weight-bold
          font-size: 95%

          &--nil
            color: $gray-light

</style>

<script lang="ts">
import Console from '~/utils/lib/core/console'
import { any, append, compose, concatAll, debounce, equals, includes, isEmpty, map, not, prop, reject, replace, toLower, toString, tapCatch, pipe_, applySpec, identity } from '~/utils/lib/core/prelude'
import { toString as toFloatString } from '~/utils/lib/core/float'
import { now, parseDatetime } from '~/utils/lib/core/datetime'
import { takeOutAccentDiacritic } from '~/utils/lib/core/string'
import { launchWindowDownload } from '~/utils/lib/core/download'
import Counter from '~/components/layout/misc/Counter.vue'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { getDetailsRoute, getCreationRoute, getDuplicationRoute } from '~/stores/hamac/project/index'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useTableProvider, type DefaultData as ProviderDefaultData } from '~/composables/layout/table-provider'
import type { UiTableWrapper } from '#components'
import { useTableSelectionMultipleLight } from '~/composables/layout/table-selection-multiple-light'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import { useStore_hamac_project_list, type Filters, type Project } from '~/stores/hamac/project/list'
import { useStore_hamac_project_tag } from '~/stores/hamac/project/tag'
import { useStore_hamac_project_contract } from '~/stores/hamac/project/contract'
import type { ComponentExposed } from 'vue-component-type-helpers'
import type { ChipSelectMultipleOption } from '~/components/ui/chip/SelectMultiple.vue'
import type { TableField } from '~/components/ui/table/Wrapper.vue'
import { SORTABLE_HANDLE_CLASS } from '~/composables/layout/sortable'

const SEARCH_EXECUTION_DEBOUNCE_DELAY = 300

const console = new Console('component:project-list')

export default defineComponent({
  components: { Counter },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Projets — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const providerDefaultData :ProviderDefaultData = {
      context: {
        order: 'updated_at',
        sort: 'desc',
        filters: {
          tags: [],
          show_archived: false,
        }
      }
    }

    const tableProvider = useTableProvider<Project, Required<Filters>>(
      ref_table,
      providerDefaultData,
    )

    const tableSelection = useTableSelectionMultipleLight<Project>(
      tableProvider.currentItems,
      (item, item_) => {
        const id  = prop('uuid_hamac_projet', item)
        const id_ = prop('uuid_hamac_projet', item_)
        return equals(id, id_)
      }
    )

    const fields = ref<TableField[]>([
      {
        key: 'actions-left',
        label: '',
        class: 'page-list__actions-left',
        manageable: false,
      },
      {
        key: 'label',
        label: 'Nom',
        sortable: true,
        class: 'page-list__label',
        manageable: true,
        displayed: true
      },
      {
        key: 'equipe_label',
        label: 'Equipe',
        class: 'page-list__team',
        manageable: true,
        displayed: true,
      },
      {
        key: 'count',
        label: 'Contrats',
        class: 'page-list__count',
        formatter: ({ value }) => toFloatString(value as number),
        manageable: true,
        displayed: true,
      },
      {
        key: 'updated_at',
        label: 'Date modification',
        class: 'page-list__date',
        formatter: ({ value }) => pipe_(
          value,
          parseDatetime(
            {
              inputFormat: undefined,
              outputFormat: 'DD/MM/YY',
            }
          ),
          replace(/\//g, ' / ') // (for "word-spacing" CSS rule)
        ),
        sortable: true,
        manageable: true,
        displayed: true
      },
      {
        key: 'created_at',
        label: 'Date création',
        class: 'page-list__date',
        formatter: ({ value }) => pipe_(
          value,
          parseDatetime(
            {
              inputFormat: undefined,
              outputFormat: 'DD/MM/YY'
            }
          ),
          replace(/\//g, ' / ') // (for "word-spacing" CSS rule)
        ),
        sortable: true,
        manageable: true,
        displayed: true
      },
      {
        key: 'tags',
        label: 'Tags',
        class: 'page-list__tags',
        manageable: true,
        displayed: true
      },
      {
        key: 'infos',
        label: 'Infos',
        class: 'page-list__infos',
        manageable: true,
        displayed: true
      },
      {
        key: 'actions-right',
        label: '',
        class: 'page-list__actions-right',
        manageable: false,
      },
    ])

    const store_project_tag = useStore_hamac_project_tag()
    const store_project_list = useStore_hamac_project_list()
    const store_project_contract = useStore_hamac_project_contract()

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...useLinkHandler(),
      fields,
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ...tableProvider,
      ...tableSelection,
      store_project_tag,
      store_project_list,
      store_project_contract,
      SORTABLE_HANDLE_CLASS,
    }
  },
  data() {
    return {
      availableTags: [] as ChipSelectMultipleOption<Project["tags"][number]>[],
      perPageOptions: [
        {
          value: 10,
          text: '10'
        },
        {
          value: 30,
          text: '30',
        },
        {
          value: 50,
          text: '50'
        }
      ],
      filters: {
        order: 'updated_at',
        sort: 'desc',
      },
      hasDoubleClicked: false,
      debouncedSearchChange: null as unknown as (_: string) => void, // (lazy)
      pendingList: [] as Array<Project["uuid_hamac_projet"]>,
      disabledList: [] as Array<Project["uuid_hamac_projet"]>, // projects disabled but not showing the pending icon
      archiveButtonPending: false,
      exportButtonPending: false,
    }
  },
  computed: {
    selectionHasArchivedProjects() {
      return any(
        (project) => project.archived,
        this.selectedItems
      )
    }
  },
  created() {
    this.fetchWrapper(
      () => {
        const uuidData = this.fromCompanyLevel().structure.uuidData
        const code_societe = this.fromCompanyLevel().company.code_societe

        const options = { uuidData, code_societe }

        return this.store_project_tag.read(options)
          .then(results => this.availableTags = map.iterable(
            applySpec({
              value: identity,
              text: identity
            }),
            results
          ))
          .catch(tapCatch(err => {
            console.warn('fetch', err)
            this.ToastBuilder
              .error(toString(err))
              .title(`Liste projets`)
              .show()
          }))
      }
    )

    this.consumer = (context) => {
      const options = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_project_list.read(options)
    }

    this.debouncedSearchChange = debounce(
      SEARCH_EXECUTION_DEBOUNCE_DELAY,
      this.setSearch
    )
  },
  methods: {
    getDuplicationRoute(uuid_hamac_projet) {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getDuplicationRoute(id, code_societe, uuid_hamac_projet)
    },
    getDetailsRoute(uuid_hamac_projet) {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getDetailsRoute(id, code_societe, uuid_hamac_projet)
    },
    getCreationRoute() {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getCreationRoute(id, code_societe)
    },
    openDetailsIntent({ uuid_hamac_projet }) {
      const hasRights = this.hasACL(
        this.ACLS.HAMAC_PROJET_GET
      )

      if (not(hasRights))
        return

      this.hasDoubleClicked = true

      const url = this.getDetailsRoute(uuid_hamac_projet)

      this.goToRoute(url)
    },
    isTagSelected(tag) {
      return any(equals(tag), this.context.filters.tags)
    },
    isTagToneDowned(tag) {
      if (isEmpty(this.context.filters.tags))
        return false

      return compose(not, this.isTagSelected)(tag)
    },
    toggleTag(tag) {
      const has = this.isTagSelected(tag)

      this.context.filters.tags = has ?
        reject(equals(tag), this.context.filters.tags) :
        append(tag, this.context.filters.tags)
    },
    isRowPending(uuid_hamac_projet) {
      return includes(uuid_hamac_projet, this.pendingList)
    },
    isRowDisabled(uuid_hamac_projet) {
      return includes(uuid_hamac_projet, this.disabledList)
    },
    archiveMultiple(projects :Project[]) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        ids: map.iterable(({ uuid_hamac_projet }) => uuid_hamac_projet, projects)
      }

      this.archiveButtonPending = true
      this.disabledList = map.iterable(
        ({ uuid_hamac_projet }) => uuid_hamac_projet,
        projects
      )

      return this.store_project_list.archive(options)
        .then(({ warningMessage }) => {

          switch (isEmpty(warningMessage)) {

            case true:
              this.ToastBuilder
                .success()
                .class('archive-project-toast')
                .title(`Projets archivés`)
                .show()
              break

            case false:
              this.ToastBuilder
                .warning(toString(warningMessage))
                .class('archive-project-toast')
                .title(`Certains projets ne sont pas archivés`)
                .show()
              break
          }

          this.refreshTable()
        })
        .catch(err => {
          console.warn('archiveCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .class('archive-project-toast')
            .title(`Archiver les projets`)
            .show()
        })
        .finally(() => {
          this.archiveButtonPending = false
          this.disabledList = []
          this.selectedItems = []
        })
    },
    archiveCallback({ uuid_hamac_projet }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        ids: [uuid_hamac_projet]
      }

      this.pendingList = append(uuid_hamac_projet, this.pendingList)

      return this.store_project_list.archive(options)
        .then(() => {
          this.ToastBuilder
            .success()
            .title(`Projet archivé`)
            .class('archive-project-toast')
            .show()
          this.refreshTable()
        })
        .catch(err => {
          console.warn('archiveCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Archiver le projet`)
            .class('archive-project-toast')
            .show()
        })
        .finally(() => {
          this.pendingList = reject(
            equals(uuid_hamac_projet),
            this.pendingList
          )
        })
    },
    exportCallback({ uuid_hamac_projet, label }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_projet
      }

      const suffix = now({
        outputFormat: 'YYYY-MM-DD-HH[h]mm'
      })

      const label_ = pipe_(
        label,
        takeOutAccentDiacritic,
        replace(/\./g, ''),
        replace(/ /g, '_'),
        toLower,
      )

      const filename = concatAll([
        `projet`,
        `_${label_}`,
        `_${suffix}`,
        `.csv`
      ])

      this.pendingList = append(uuid_hamac_projet, this.pendingList)

      return this.store_project_contract.export(options)
        .then(data => launchWindowDownload(data, filename))
        .catch(err => {
          console.warn('exportCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Exporter les données du projet`)
            .show()
        })
        .finally(() => {
          this.pendingList = reject(
            equals(uuid_hamac_projet),
            this.pendingList
          )
        })
    },
    exportMultiple(selectedItems :Project[]) {
      this.exportButtonPending = true
      this.disabledList = map.iterable(
        ({ uuid_hamac_projet }) => uuid_hamac_projet,
        selectedItems
      )

      if (this.disabledList.length === 0) {
        this.ToastBuilder
          .warning('Vous pouvez sélectionner des projets en cliquant dessus')
          .title(`Aucun projet sélectionné`)
          .show()
        this.exportButtonPending = false
        return
      }

      return Promise.all(
        map.iterable(
          ({ uuid_hamac_projet, label }) => {
            const options = {
              uuidData: this.fromCompanyLevel().structure.uuidData,
              code_societe: this.fromCompanyLevel().company.code_societe,
              uuid_hamac_projet
            }

            const suffix = now({
              outputFormat: 'YYYY-MM-DD-HH[h]mm'
            })

            const label_ = pipe_(
              label,
              takeOutAccentDiacritic,
              replace(/\./g, ''),
              replace(/ /g, '_'),
              toLower,
            )

            const filename = concatAll([
              `projet`,
              `_${label_}`,
              `_${suffix}`,
              `.csv`
            ])

            return this.store_project_contract.export(options)
            .then(data => launchWindowDownload(data, filename))
          },
          selectedItems
        )
      )
      .then(() => {
        this.ToastBuilder
          .success()
          .title(`Export des données réussi`)
          .show()
      })
      .catch(err => {
        console.warn('exportCallback', err)
        this.ToastBuilder
          .error(toString(err))
          .title(`Exporter des données des projets`)
          .show()
      })
      .finally(() => {
        this.exportButtonPending = false
        this.disabledList = []
        this.selectedItems = []
      })
    },
  }
})
</script>
